import { useEffect, useState, type ReactNode } from "react";
import { Wordmark } from "./Wordmark";
import {
  FiltersProvider,
  useAnalyticsClient,
  type AnalyticsClient,
  type OrgStructure,
} from "../../analytics";
import { AppLoadingScreen } from "./AppLoadingScreen";

interface BootData {
  org: OrgStructure;
}

/* Boot fetch (org structure) keyed by the client instance — stable per
 * workspace, since AnalyticsProvider is keyed by workspace id. This collapses
 * the concurrent double-mount React 18 StrictMode triggers in dev (and any
 * remount race) into a single request. Unlike the page hooks, it doesn't go
 * through useQuery, so it needs its own in-flight guard. */
const bootInflight = new WeakMap<AnalyticsClient, Promise<BootData>>();

function bootFetch(client: AnalyticsClient): Promise<BootData> {
  const existing = bootInflight.get(client);
  if (existing) return existing;

  const p = client
    .getOrgStructure()
    .then((org) => ({ org }))
    .finally(() => {
      if (bootInflight.get(client) === p) bootInflight.delete(client);
    });
  bootInflight.set(client, p);
  return p;
}

/* Loads the org structure once, then mounts the filters provider. A branded
 * splash covers the (brief) first fetch. We only show the boot error screen
 * when the service is genuinely unreachable; otherwise the windowed views load
 * and surface their own actionable errors. */
export function DashboardBoot({ children }: { children: ReactNode }) {
  const client = useAnalyticsClient();
  const [data, setData] = useState<BootData | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let active = true;
    bootFetch(client)
      .then(({ org }) => {
        if (active) setData({ org });
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, [client]);

  if (error) {
    return (
      <BootScreen>
        <p className="text-body-sm text-error-fg">
          Couldn’t reach the analytics service. Check your connection and
          reload.
        </p>
      </BootScreen>
    );
  }

  if (!data) {
    return <AppLoadingScreen />;
  }

  return <FiltersProvider org={data.org}>{children}</FiltersProvider>;
}

/** Branded full-screen frame for boot/resolve states (also used by the
 *  workspace shell while the workspaces list loads). Shares the AppLoadingScreen
 *  palette + [Gateway] wordmark so these status/error screens read as the same
 *  family as the loading splash. Self-contained (inlined palette/font) so it can
 *  render before any workspace/theme context exists — matching the loader. */
export function BootScreen({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex h-screen flex-col items-center justify-center gap-8"
      style={{
        background: "var(--page, #FBFBFB)",
        fontFamily:
          "'Söhne','Inter','DM Sans',ui-sans-serif,system-ui,sans-serif",
      }}
    >
      <Wordmark
        height={26}
        label="Gateway"
        color="var(--text, #140309)"
        bracket="var(--text-muted, #827C7E)"
      />
      {children}
    </div>
  );
}

export function LoadingDots() {
  return (
    <div className="flex gap-1.5" role="status" aria-label="Loading">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="size-2 animate-pulse rounded-full bg-grey-400"
          style={{ animationDelay: `${i * 160}ms` }}
        />
      ))}
    </div>
  );
}
